<template>
  <div class="d-flex justify-content-between">
    <div class="search-field-container">
      <div class="search-field">
        <div class="d-flex justify-content-between align-items-center">
          <div class="search-input">
            <input type="text"
                   class="form-control"
                   placeholder="Sök platser, personer, ämnen mm..."
                   @keyup.enter="handleGotoSearch"
                   @keyup.prevent="handleKeyUp"
                   @focus="focused = true"
                   v-model="query"
                   aria-label="Sökfält"
            />
          </div>
          <div class="search-buttons">
            <button v-if="!_.isEmpty(query)" class="mr-2 clear no-style-btn" @click="clearInput"><span>Rensa</span> <i class="fas fa-times-circle" /></button>
            <button type="submit" class="btn btn-secondary search-btn" @click="handleGotoSearch" aria-label="Sök"><i class="fas fa-search" /> <span class="search-label">Sök</span></button>
          </div>
        </div>
      </div>
      <div v-if="focused" class="results">
        <div v-if="showResults && !onSearchPage">
          <div v-if="searching" class="loader">
            <i class="fas fa-spin fa-spinner" />
          </div>

          <div class="row">
            <div v-if="!searching" class="py-3 border-top col-12">
              {{ searchResults.matches || '0' }} kulturarv hittades för <strong>{{ this.query }}</strong>
            </div>
            <div v-if="searchResults.locations && searchResults.locations.count > 0" class="py-3 border-top d-flex flex-column col-lg-6">
              <h6 class="text-uppercase">Platser</h6>
              <a :key="location.id" v-for="location in _.slice(searchResults.locations.result, 0, 3)" :href="location.resource_url">
                <i class="fas fa-map-marker mr-2" />
                {{ location.name }}
              </a>
              <a href="#" v-if="searchResults.locations.count > 3" @click="handleGotoSearch">
                <i class="fas fa-plus mr-2" />
                {{ searchResults.locations.count - 3 }} platser
              </a>
            </div>
            <div v-if="searchResults.articles && searchResults.articles.count > 0" class="py-3 border-top d-flex flex-column col-lg-6">
              <h6 class="text-uppercase">Artiklar</h6>
              <a :key="article.id" v-for="article in _.slice(searchResults.articles.result, 0, 3)" :href="article.resource_url">
                <i class="fas fa-file mr-2" />
                {{ article.title }}
              </a>
              <a href="#" v-if="searchResults.articles.count > 3" @click="handleGotoSearch">
                <i class="fas fa-plus mr-2" />
                {{ searchResults.articles.count - 3 }} artiklar
              </a>
            </div>

            <div v-if="searchResults.images && searchResults.images.count > 0" class="py-3 border-top d-flex flex-column col-lg-6">
              <h6 class="text-uppercase">Bilder</h6>
              <a :key="image.id" v-for="image in _.slice(searchResults.images.result, 0, 3)" :href="image.resource_url">
                <i class="fas fa-image mr-2" />
                {{ image.title }}
              </a>
              <a href="#" v-if="searchResults.images.count > 3" @click="handleGotoSearch">
                <i class="fas fa-plus mr-2" />
                {{ searchResults.images.count - 3 }} bilder
              </a>
            </div>

            <div v-if="searchResults.videos && searchResults.videos.count > 0" class="py-3 border-top d-flex flex-column col-lg-6">
              <h6 class="text-uppercase">Filmer</h6>
              <a :key="video.id" v-for="video in _.slice(searchResults.videos.result, 0, 3)" :href="video.resource_url">
                <i class="fas fa-video mr-2" />
                {{ video.title }}
              </a>
              <a href="#" v-if="searchResults.videos.count > 3" @click="handleGotoSearch">
                <i class="fas fa-plus mr-2" />
                {{ searchResults.videos.count }} bilder
              </a>
            </div>

            <div v-if="searchResults.categories && searchResults.categories.count > 0" class="py-3 border-top d-flex flex-column col-lg-6">
              <h6 class="text-uppercase">Ämneskategorier</h6>
              <a :key="category.id" v-for="category in searchResults.categories.result">
                <div class="badge badge-primary">{{ category.title }}</div>
              </a>
            </div>
            <div v-if="searchResults.addressSuggestions" class="py-3 border-top d-flex flex-column col-lg-6">
              <h6 class="text-uppercase">Matchande adresser</h6>
              <a :key="suggestion.objektidentitet" v-for="suggestion in searchResults.addressSuggestions">
                <div class="badge badge-primary">{{ suggestion.address }}</div>
              </a>
            </div>
            <div class="col-12 text-center">
              <b-button @click="handleGotoSearch">Visa alla resultat</b-button>
            </div>
          </div>
        </div>
        <div v-else-if="_.isEmpty(query) || query === ''">
          <div class="pt-1 pb-3">
            <h6 class="text-uppercase">Populära sökningar just nu</h6>
            <b-badge variant="primary" :key="searchTerm.id" v-for="searchTerm in searchTerms" :href="`${baseUrl}/sok?q=${searchTerm.term}`" @click.prevent="handlePopularClick(searchTerm.term)" class="mr-2 mb-1">
              <h6 class="m-0 text-white">{{ searchTerm.term }}</h6>
            </b-badge>
          </div>
          <div class="py-3 border-top" v-if="locations">
            <h6 class="text-uppercase">Mest besökta platser</h6>
            <b-badge variant="light" :key="location.id" v-for="location in locations" :href="location.resource_url" class="mr-2 mb-1">
              <h6 class="m-0">{{ location.name }}</h6>
            </b-badge>
          </div>
        </div>
        <div v-else-if="suggestions.title">
          <div :key="suggestion" v-for="suggestion in suggestions.title" class="suggestion" @click="handleSuggestionClick(suggestion)">
            <i class="fas fa-search"></i> {{ suggestion }}
          </div>
        </div>
      </div>
    </div>

    <button id="near-me" class="near-me no-style-btn" :class="!geoLocation && 'no-geolocation'" role="button" @click="handleNearMeClick" aria-label="Nära mig">
      <i class="fas fa-map-marked-alt" />
      <span class="d-none d-sm-inline-block ml-1">Nära mig</span>
    </button>
    <b-tooltip v-if="!geoLocation" target="near-me">
      Du måste ge webbläsaren tillgång till din plats först.
    </b-tooltip>
  </div>
</template>

<script>
/* global axios, Bus */
import { mapState } from 'vuex'
import 'url-search-params-polyfill'
import store from '../../store'
import { getUrl } from '../../helpers'
const CancelToken = axios.CancelToken
const searchCancelArr = []
const suggestCancelArr = []
export default {
  props: [],

  data () {
    return {
      query: '',
      prevSearch: {},
      prevSuggest: {},
      searching: false,
      focused: false,
      baseUrl: process.env.MIX_ASSET_URL,
      suggestions: [],
      searchParams: new URLSearchParams(window.location.search),
      mounted: false
    }
  },

  async mounted () {
    await store.restored

    this.query = this.searchParams.has('q') ? this.searchParams.get('q') : this.searchQuery

    const filters = this.getFilters()
    this.$store.commit('setFilters', filters)

    Bus.$on('do-perform-search', () => this.performSearch(this.query))
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('focus', this.handleClickOutside, true)

    this.mounted = true

    this.performSearch(this.query)

    if (this.onSearchPage && !this.searchParams.has('q')) {
      this.searchParams.set('q', this.query)
      window.history.pushState(null, null, `?${this.searchParams.toString()}`)
    }

    axios.get(`${process.env.MIX_ASSET_URL}/api/popular`)
      .then(response => response.data)
      .then(popular => this.$store.commit('setPopular', popular))

    if (this.query !== '' && _.isEmpty(this.suggestions)) {
      this.getSuggestions()
    }
  },
  destroyed () {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('focus', this.handleClickOutside)
  },

  methods: {
    shouldDoSearch () {
      return (this.query !== this.searchQuery || _.get(this.searchResults, 'matches') === 0 || _.isEmpty(this.searchResults))
    },
    getFilters () {
      const queryFilters = {}
      const blacklist = ['q', 'municipality']
      for (const p of this.searchParams) {
        if (!_.includes(blacklist, p[0])) {
          queryFilters[p[0]] = p[1].split(',')
        }
      }

      if (queryFilters.timePeriods) {
        queryFilters.timePeriods = parseInt(queryFilters.timePeriods)
      }

      return { ...this.filters, ...queryFilters }
    },
    handleNearMeClick () {
      const onSearch = _.includes(window.location.pathname, 'sok')
      const onMapTab = _.includes(window.location.pathname, 'karta')

      if (this.geoLocation) {
        if (!onSearch) {
          window.location.href = getUrl('/sok/karta/nearMe')
        } else if (onSearch && !onMapTab) {
          Bus.$emit('switchTab', 0)
          setTimeout(function () {
            Bus.$emit('nearMe', true)
          }, 500)
        } else {
          Bus.$emit('nearMe', true)
        }
      }
    },
    handlePopularClick (searchTerm) {
      this.query = searchTerm
      this.handleGotoSearch()
    },
    handleSuggestionClick (suggestion) {
      this.query = suggestion
      this.handleGotoSearch()
    },
    handleGotoSearch () {
      if (this.onSearchPage) {
        this.focused = false
        this.search()
      } else {
        window.location.href = `${process.env.MIX_ASSET_URL}/sok?q=${this.query}`
      }
    },
    getSuggestions () {
      const s = _.debounce(this.performGetSuggestions, 300, {
        leading: false,
        trailing: true
      })

      if (this.prevSuggest.cancel) {
        this.prevSuggest.cancel()
      }

      this.prevSuggest = s
      s()
    },
    performGetSuggestions () {
      try {
        if (suggestCancelArr.length > 0) {
          _.forEach(suggestCancelArr, c => typeof c === 'function' && c())
        }
        // !!value && axios.get('/api/search?q=' + value)
        axios.post(`${process.env.MIX_ASSET_URL}/api/suggest`, {
          search_query: this.query
        }, {
          cancelToken: new CancelToken(function executor (c) {
            suggestCancelArr.push(c)
          })
        })
          .then(response => {
            this.suggestions = response.data
          })
      } catch (e) {
        if (suggestCancelArr.length > 0) {
          _.forEach(suggestCancelArr, c => typeof c === 'function' && c())
        }
        return [e]
      }
    },
    handleKeyUp: function (e) {
      // console.log(e.code)
      if (e.code !== 'Tab') {
        this.onSearchPage ? this.getSuggestions() : this.search()
      }
    },
    handleClickOutside (evt) {
      if (!this.$el.contains(evt.target)) {
        this.focused = false
      }
    },
    search (force = false) {
      this.query = this.query.trim()
      this.$store.dispatch('setSavedViewExtent', {
        search: undefined
      })
      const s = _.debounce(this.performSearch, 300, {
        leading: false,
        trailing: true
      })

      if (this.prevSearch.cancel) {
        this.prevSearch.cancel()
      }

      this.prevSearch = s
      this.searchParams.set('q', this.query)
      this.onSearchPage && window.history.pushState(null, null, `?${this.searchParams.toString()}`)

      if (this.shouldDoSearch() || force) {
        s(this.query)
      }
    },

    performSearch (value) {
      this.$store.dispatch('setSearchResults', [])
      // Bus.$emit('searchResults', [])
      this.$store.dispatch('setSearchQuery', value)
      this.searching = true
      try {
        if (searchCancelArr.length > 0) {
          _.forEach(searchCancelArr, c => typeof c === 'function' && c())
        }
        // !!value && axios.get('/api/search?q=' + value)
        axios.post(`${process.env.MIX_ASSET_URL}/api/search`, {
          search_query: value,
          filters: this.filters,
          address_locations: false
        }, {
          cancelToken: new CancelToken(function executor (c) {
            searchCancelArr.push(c)
          })
        })
          .then(response => {
            this.searching = false
            this.$store.dispatch('setSearchResults', response.data)
            // Bus.$emit('searchResults', response.data)
          })
      } catch (e) {
        this.searching = false
        if (searchCancelArr.length > 0) {
          _.forEach(searchCancelArr, c => typeof c === 'function' && c())
        }
        return [e]
      }
    },

    clearInput () {
      this.query = ''
      this.$store.dispatch('setSavedViewExtent', {
        search: undefined
      })
      this.search()
    }
  },

  computed: mapState({
    showResults: function () {
      return this.focused && (this.searching || !_.isEmpty(this.searchResults) && !(_.isEmpty(this.query) || this.query === ''))
    },
    onSearchPage: function () {
      return window.location.href.indexOf('/sok') !== -1
    },
    searchQuery: state => state.search.searchQuery,
    filters: state => state.search.filtered,
    searchResults: state => state.search.searchResults,
    locations: state => state.search.popular.locations,
    searchTerms: state => state.search.popular.searchTerms,
    geoLocation: state => state.map.geoLocation
  }),

  watch: {
    searching: function (newData, oldData) {
      Bus.$emit('isSearching', newData)
    },
    searchQuery: function (newData, oldData) {
      if (newData) {
        this.query = newData
      }
    },
    filters: function (newData) {
      if (this.mounted && this.onSearchPage) {
        const oldSearchParams = this.searchParams.toString()
        _.each(newData, (f, key) => {
          if (_.size(f) > 0 || (typeof f === 'number' && f > 0)) {
            const v = typeof f === 'object' ? f.join(',') : f
            this.searchParams.set(key, v)
          } else {
            this.searchParams.delete(key)
          }
        })
        window.history.pushState(null, null, `?${this.searchParams.toString()}`)
        oldSearchParams !== this.searchParams.toString() && this.search(true)
      }
    }
  }
}
</script>
